// hooks/useSearchWithDebounce.ts
import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { SEARCH_DEBOUNCE_MS } from '../utils/constants'

interface UseSearchWithDebounceProps<T> {
  items: T[]
  searchFields: (keyof T)[]
  debounceMs?: number
}

export function useSearchWithDebounce<T>({
  items,
  searchFields,
  debounceMs = SEARCH_DEBOUNCE_MS,
}: UseSearchWithDebounceProps<T>) {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const debounceTimerRef = useRef<NodeJS.Timeout | number | null>(null)

  const handleSearch = useCallback(
    (value: string) => {
      setSearchQuery(value)

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        setDebouncedSearchQuery(value)
      }, debounceMs)
    },
    [debounceMs],
  )

  const clearSearch = useCallback(() => {
    setSearchQuery('')
    setDebouncedSearchQuery('')
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
  }, [])

  const filteredItems = useMemo((): T[] => {
    if (!debouncedSearchQuery.trim()) {
      return items
    }

    const query = debouncedSearchQuery.toLowerCase().trim()

    return items.filter((item) => {
      return searchFields.some((field) => {
        const value = item[field]
        if (value === undefined || value === null) return false
        return String(value).toLowerCase().includes(query)
      })
    })
  }, [items, debouncedSearchQuery, searchFields])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return {
    searchQuery,
    handleSearch,
    clearSearch,
    filteredItems,
    isSearching: debouncedSearchQuery.trim().length > 0,
  }
}
